import { inject, Injectable } from '@angular/core';
import { Club, LicenseRateSeason, Season, Team } from '@models';
import { SeasonsService } from './seasons.service';

@Injectable({
  providedIn: 'root',
})
export class InscriptionsTeamsService {
  private readonly seasonService = inject(SeasonsService);

  async getTeamPrice(
    teams: Partial<Team & { price: number }>[],
    seasons: Season[],
    seasonId: string,
    club: Club,
    registeredTeams: Partial<Team>[],
  ): Promise<{ price: number; scope: string } | null> {
    const selectedSeason = seasons.find((s) => s.id === seasonId);
    let groupTeamsByScope: { [scope: string]: number } = {};

    if (registeredTeams.length) {
      groupTeamsByScope = this.groupTeamsByScope(registeredTeams);
    }

    if (selectedSeason && !groupTeamsByScope[selectedSeason.scope]) {
      groupTeamsByScope[selectedSeason.scope] = 0;
    }

    let priceToAdd = 0;
    // Lectura fresca del servidor (evita el rate cacheado de la IndexedDB).
    const rates = await this.seasonService.getRatesOnce(seasonId);

    if (rates?.teamRates && selectedSeason) {
      const scope = selectedSeason.scope ?? '';
      const existingInScope = scope
        ? (groupTeamsByScope[scope] ?? 0)
        : registeredTeams.filter((t) => t.seasonId === seasonId).length;
      const cartInScope = scope
        ? teams.filter((t) => t.scope === scope).length
        : teams.filter((t) => t.seasonId === seasonId).length;
      const teamsInScope = existingInScope + cartInScope;

      // Ausente/vacío/no numérico → 0 (sin umbral, todos pagan). Sin este
      // saneado, `Number(undefined)` daría NaN y todas las comparaciones de
      // abajo serían false → `shouldCharge` false → cada equipo saldría gratis.
      const parsedFreeTeamsFrom = Number(rates.teamRates.freeTeamsFrom);
      const freeTeamsFrom = Number.isFinite(parsedFreeTeamsFrom)
        ? parsedFreeTeamsFrom
        : 0;
      // freeTeamsFrom = 0 → no free threshold, all teams pay.
      // freeTeamsFrom = N → first N teams pay; from team N+1 onwards it's free.
      // El conteo es por `scope` a propósito: el cupo gratis se comparte entre
      // todas las competiciones del mismo ámbito (p. ej. todas las MIXTAS).
      const shouldCharge = freeTeamsFrom === 0 || teamsInScope < freeTeamsFrom;
      if (shouldCharge) {
        priceToAdd = club.hasSchool
          ? Number(rates.teamRates.school)
          : Number(rates.teamRates.standard);
      }

      return { price: priceToAdd, scope };
    }

    return null;
  }

  private groupTeamsByScope(registeredTeams: Partial<Team>[]) {
    return registeredTeams.reduce(
      (acc, team) => {
        const scope = team.scope ?? 'undefined';
        acc[scope] = (acc[scope] ?? 0) + 1;
        return acc;
      },
      {} as { [scope: string]: number },
    );
  }
}
